import React, { useState, useEffect, useRef } from 'react';
import { getPlayerDisplayName } from './playerDisplay';

/**
 * Composant d'affichage du score live d'un match de tennis
 * Affiche les noms des joueurs, le score du jeu en cours et les scores des sets
 * 
 * @param {Object} match - Les informations du match (joueur1Nom, joueur2Nom, formatMatch, etc.)
 * @param {Object} currentScore - Le score actuel du match
 * @param {string} currentScore.jeuScore - Score du jeu en cours (ex: "30-15")
 * @param {Array} currentScore.sets - Tableau des scores de sets [[6,4], [3,6], ...]
 * @param {string} currentScore.server - Serveur actuel ("J1" ou "J2")
 * @param {Array} currentScore.lastTieBreakScores - Scores des tie-breaks [[7,5], ...]
 * @param {boolean} currentScore.tieBreakActive - Tie-break en cours
 */
function LiveScoreDisplay({ match, currentScore, points = [], onScoreInit = null }) {
  // 🆕 États pour les animations
  const [slideJeuScore, setSlideJeuScore] = useState(false); // Slide sur le score du jeu
  const [slideSet, setSlideSet] = useState(null); // Index du set qui change
  
  // 🆕 Références pour détecter les changements
  const prevScoreRef = useRef(null);
  
  // 🆕 Détection des changements et déclenchement des animations
  useEffect(() => {
    if (!currentScore || !prevScoreRef.current) {
      prevScoreRef.current = currentScore;
      return;
    }
    
    const prev = prevScoreRef.current;
    const curr = currentScore;
    
    // 1️⃣ DÉTECTION CHANGEMENT DE POINT (slide animation sur le score du jeu)
    if (prev.jeuScore !== curr.jeuScore) {
      setSlideJeuScore(true);
      setTimeout(() => setSlideJeuScore(false), 400);
    }
    
    // 2️⃣ DÉTECTION CHANGEMENT DE JEU/SET (slide animation)
    if (prev.sets && curr.sets) {
      for (let i = 0; i < curr.sets.length; i++) {
        const prevSet = prev.sets[i];
        const currSet = curr.sets[i];
        
        if (prevSet && currSet) {
          // Changement dans un set → slide
          if (prevSet[0] !== currSet[0] || prevSet[1] !== currSet[1]) {
            setSlideSet(i);
            setTimeout(() => setSlideSet(null), 400);
          }
        }
      }
    }
    
    prevScoreRef.current = currentScore;
  }, [currentScore]);
  
  // Calcul du nombre max de sets selon le format du match
  const maxSets = match?.formatMatch === "BEST_OF_5" ? 5 : 3;
  const nomMaxWidth = match?.formatMatch === "BEST_OF_5" ? "150px" : "200px";

  // ✅ LE SERVEUR EST DÉJÀ CALCULÉ CORRECTEMENT DANS TennisScoreEngine
  const displayServer = currentScore?.server || 'J1';

  return (
    <>
      {/* 🆕 Styles CSS pour les animations */}
      <style>{`
        @keyframes slideUp {
          0% { 
            transform: translateY(5px);
            opacity: 0.5;
          }
          100% { 
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .slide-up {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
      
      <div
        style={{
          backgroundColor: "rgba(0,0,0,0.8)",
          color: "white",
          borderRadius: "8px",
          padding: "12px 16px",
          fontSize: "11px",
          marginBottom: "6px", 
          zIndex: 10,
          position: "relative",
        }}
      >

      {/* Ligne entêtes sets */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `3fr 0.8fr ${Array(maxSets).fill("0.6fr").join(" ")}`,
          gap: "0px",
          marginBottom: "2px",
          opacity: 0.8,
          alignItems: "center",
        }}
      >
        <div>
          {onScoreInit && (
            <button
              onClick={onScoreInit}
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.7)',
                borderRadius: '5px',
                color: 'rgba(255,255,255,0.85)',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: '600',
                padding: '2px 6px',
                lineHeight: 1.3,
                display: 'block',
                marginLeft: 0,
              }}
            >
              ⚡ Modifier le score
            </button>
          )}
        </div>
        <div style={{ textAlign: "center" }}>Jeu</div>
        {Array.from({ length: maxSets }).map((_, i) => (
          <div key={`header-${i}`} style={{ textAlign: "center" }}>
            S{i + 1}
          </div>
        ))}
      </div>

      {/* Joueur 1 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `3fr 0.8fr ${Array(maxSets).fill("0.6fr").join(" ")}`,
          gap: "0px",
          alignItems: "center",
          marginBottom: "1px",
        }}
      >
        <div
          style={{
            fontWeight: displayServer === "J1" ? "800" : "600",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: nomMaxWidth,
            color: "#1976D2",
            textAlign: "left",
            fontSize: "14px",
          }}
        >
          {displayServer === "J1" ? "● " : ""}{getPlayerDisplayName(match, "J1")}
        </div>
        <div
          className={slideJeuScore ? 'slide-up' : ''}
          style={{
            textAlign: "center",
            fontWeight: "700",
          }}
        >
          {currentScore?.jeuScore?.split("-")[0] || "0"}
        </div>
        {Array.from({ length: maxSets }).map((_, i) => {
          const set = currentScore?.sets?.[i];
          const tbData = currentScore?.lastTieBreakScores?.[i];
          const tb = tbData && Array.isArray(tbData) && tbData[0] !== undefined 
            ? `(${tbData[0]})` 
            : "";

          const val = set ? set[0] : "";
          const isSetFinished = set && (set[0] > 0 || set[1] > 0); 

          return (
            <div
              key={`mini-j1-${i}`}
              className={slideSet === i ? 'slide-up' : ''}
              style={{
                textAlign: "center",
                fontSize: "11px",
              }}
            >
              <span style={{ fontWeight: isSetFinished ? "700" : "400" }}>
              {val}
              </span>
              {tb && (
                <span style={{ fontSize: "11px", opacity: 0.9, fontWeight: "400" }}>
                  {" "}{tb}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Joueur 2 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `3fr 0.8fr ${Array(maxSets).fill("0.6fr").join(" ")}`,
          gap: "0px",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontWeight: displayServer === "J2" ? "800" : "600",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: nomMaxWidth,
            color: "#FF9800",
            textAlign: "left",
            fontSize: "14px",
          }}
        >
          {displayServer === "J2" ? "● " : ""}{getPlayerDisplayName(match, "J2")}
        </div>
        <div
          className={slideJeuScore ? 'slide-up' : ''}
          style={{
            textAlign: "center",
            fontWeight: "700",
          }}
        >
          {currentScore?.jeuScore?.split("-")[1] || "0"}
        </div>
        {Array.from({ length: maxSets }).map((_, i) => {
          const set = currentScore?.sets?.[i];
          const tbData = currentScore?.lastTieBreakScores?.[i];
          const tb = tbData && Array.isArray(tbData) && tbData[1] !== undefined 
            ? `(${tbData[1]})` 
            : "";
          const val = set ? set[1] : "";
          const isSetFinished = set && (set[0] > 0 || set[1] > 0);

          return (
            <div
              key={`mini-j2-${i}`}
              className={slideSet === i ? 'slide-up' : ''}
              style={{
                textAlign: "center",
                fontSize: "11px",
              }}
            >
              <span style={{ fontWeight: isSetFinished ? "700" : "400" }}>
              {val}
              </span>
              {tb && (
                <span style={{ fontSize: "11px", opacity: 0.9, fontWeight: "400" }}>
                  {" "}{tb}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {/* 🆕 HISTORIQUE 12 DERNIERS POINTS - 2 lignes (J1 + J2) */}
      {points && points.length > 0 && (() => {
        const last12 = points.slice(-12);
        const streaks = last12.map((pt, i) => {
          let s = 1;
          for (let j = i - 1; j >= 0; j--) {
            if (last12[j].winner === pt.winner) s++;
            else break;
          }
          return s;
        });
        // Toujours 12 slots pour garder une largeur fixe dès le 1er point
        const slots = Array.from({ length: 12 }, (_, i) => {
          const pt = last12[i] || null;
          return { pt, streak: streaks[i] || 0 };
        });
        return (
          <div style={{
            marginTop: '8px',
            paddingTop: '6px',
            borderTop: '1px solid rgba(255,255,255,0.15)',
          }}>
            {/* Ligne J1 */}
            <div style={{ display: 'flex', gap: '3px', marginBottom: '2px' }}>
              {slots.map(({ pt, streak }, i) => {
                const opacity = pt && pt.winner === 'J1'
                  ? Math.min(1, 0.5 + (streak - 1) * (0.5 / 11))
                  : 0;
                return (
                  <div key={i} style={{
                    flex: 1,
                    height: '4px',
                    borderRadius: '2px',
                    backgroundColor: '#1976D2',
                    opacity,
                  }} />
                );
              })}
            </div>
            {/* Ligne J2 */}
            <div style={{ display: 'flex', gap: '3px' }}>
              {slots.map(({ pt, streak }, i) => {
                const opacity = pt && pt.winner === 'J2'
                  ? Math.min(1, 0.5 + (streak - 1) * (0.5 / 11))
                  : 0;
                return (
                  <div key={i} style={{
                    flex: 1,
                    height: '4px',
                    borderRadius: '2px',
                    backgroundColor: '#FF9800',
                    opacity,
                  }} />
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
    </>
  );
}

export default LiveScoreDisplay;